import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant =
  | 'default'
  | 'primary'
  | 'ai'
  | 'outline-ai'
  | 'ghost'
  | 'danger';

export type ButtonSize = 'default' | 'sm';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Classe PrimeIcons (ex: 'pi pi-plus') que aparece à esquerda do label */
  icon?: string;
  /** Mostra spinner e desabilita */
  loading?: boolean;
  children?: ReactNode;
  className?: string;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  default:
    'bg-[var(--bg)] text-[var(--fg)] border-[var(--border)] hover:bg-[var(--bg-hover)] hover:border-[var(--border-strong)]',
  primary:
    'bg-[var(--brand-600)] text-white border-[var(--brand-600)] hover:bg-[var(--brand-700)] hover:border-[var(--brand-700)]',
  ai:
    'bg-[var(--ai-500)] text-white border-[var(--ai-500)] hover:bg-[var(--ai-600)] hover:border-[var(--ai-600)]',
  'outline-ai':
    'bg-[var(--ai-50)] text-[var(--ai-600)] border-[var(--ai-200)] hover:bg-[var(--ai-100)]',
  ghost:
    'bg-transparent text-[var(--fg-muted)] border-transparent hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]',
  danger:
    'bg-[var(--bg)] text-[var(--error-700)] border-[var(--border)] hover:bg-[var(--error-50)] hover:border-[var(--error-100)]',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  default: 'px-3 py-1.5 text-[13px]',
  sm: 'px-2 py-1 text-[12px]',
};

/**
 * Botão atômico do design system com 6 variantes + 2 tamanhos.
 *
 * @example
 * <Button variant="primary" icon="pi pi-plus">Novo processo</Button>
 * <Button variant="ai" loading>Gerando documento...</Button>
 * <Button variant="ghost" size="sm">Cancelar</Button>
 */
export default function Button({
  variant = 'default',
  size = 'default',
  icon,
  loading = false,
  disabled = false,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  const baseClasses =
    'inline-flex items-center justify-center gap-1.5 rounded-md border font-medium leading-[1.3] cursor-pointer transition-all duration-[120ms] whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-600)] focus-visible:ring-offset-1 disabled:opacity-60 disabled:cursor-not-allowed';
  const variantClass = VARIANT_CLASSES[variant];
  const sizeClass = SIZE_CLASSES[size];
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      className={`${baseClasses} ${variantClass} ${sizeClass} ${className ?? ''}`}
      {...rest}
    >
      {loading ? (
        <i className="pi pi-spin pi-spinner text-[13px]" aria-hidden="true" />
      ) : icon ? (
        <i className={`${icon} text-[13px]`} aria-hidden="true" />
      ) : null}
      {children}
    </button>
  );
}
